import {FullHeightCard} from "../../components/cards";
import React, {useEffect, useRef, useState} from "react";
import RepApi from "../../services/RepApi";
import {Descriptions, Divider, message, Space, Spin, Tabs} from "antd";
import {useHistory} from "react-router-dom";
import {ArrowLeftOutlinedButton, LinkButton, LinkTextButton} from "../../components/buttons/SimpleButtons";
import {JsonEditor} from "jsoneditor-react";
import {DownloadButton} from "../../components/buttons/DownloadButton";
import {
    CreateReportModal,
    extractedReportAttributes,
    mapApiTemplateModelToCreateModel
} from "../reports/CreateReportModal";
import {isReportInProgress, REPORT_STATUS, reportStatusTag} from "../reports/reportStatus";
import axios from "axios";
import * as XLSX from 'xlsx';
import {AgGridReact} from 'ag-grid-react';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';



function useReportData(id) {
    const [data, setData] = useState(null);
    const loadErrorShown = useRef(false);
    useEffect(() => {
        let cancelled = false;
        const checkProgress = async () => {
            try {
                const res = await RepApi.get(`/reports/${id}`);
                const result = res.data;
                if (cancelled) {
                    return true;
                }
                loadErrorShown.current = false;
                setData(result);
                return !isReportInProgress(result.status);
            } catch (e) {
                console.error(e);
                if (!cancelled && !loadErrorShown.current) {
                    message.error('Failed to load report');
                    loadErrorShown.current = true;
                }
                return false;
            }
        };
        const run = async () => {
            if (await checkProgress()) {
                clearInterval(intervalId);
            }
        };
        const intervalId = setInterval(run, 2000);
        void run();
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [id]);
    return data;
}

export function Report({id}) {
    const data = useReportData(id);
    const history = useHistory();
    const prevStatus = useRef(null);

    useEffect(() => {
        const status = data?.status;
        if (
            status === REPORT_STATUS.FAILED
            && prevStatus.current != null
            && isReportInProgress(prevStatus.current)
        ) {
            message.error('Report failed');
        }
        if (status != null) {
            prevStatus.current = status;
        }
    }, [data?.status]);
    return (
        <FullHeightCard
            title={(
                <Space>
                    <ArrowLeftOutlinedButton
                        type={"circle"}
                        style={{marginRight: '8px'}}
                        size={"small"}
                        onClick={_ => history.push("/reports")}
                    />
                    Report {data?.id}
                </Space>
            )}
            extra={data && (
                <ReRun data={data}/>
            )}
        >
            <Spin spinning={data == null} style={{height: '100%'}}>
                <Descriptions column={1}>
                    <Descriptions.Item label={"Title"}>
                        {data?.title}
                    </Descriptions.Item>
                    <Descriptions.Item label={"Created At"}>
                        {data?.created_at}
                    </Descriptions.Item>
                    <Descriptions.Item label={"Status"}>
                        {reportStatusTag(data?.status)}
                    </Descriptions.Item>
                    <Descriptions.Item label={"Template"}>
                        <a href={`/templates/${data?.template}`}>View</a>
                    </Descriptions.Item>
                </Descriptions>
                {data?.status === REPORT_STATUS.COMPLETED && (
                    <DownloadButton url={data?.output} title="Download Report" type="primary"/>
                )}
                <Divider/>
                {data?.parameters && data?.id && (
                    <JsonEditor key={data.id} name="parameters" value={data?.parameters ?? {}} mode={"view"}/>
                )}
                <Divider/>
                {data?.status === REPORT_STATUS.COMPLETED && (
                    <Preview data={data}/>
                )}
            </Spin>
        </FullHeightCard>
    );
}


function Preview({data}) {
    const [tabs, setTabs] = useState([]);
    const [parsedTableReport, setParsedTableReport] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const handlePreviewClick = _ => {
        setIsLoading(true);
        axios.get(data.output, {responseType: 'arraybuffer'})
            .then(response => {
                const workbook = XLSX.read(response.data, {type: 'array'});
                const parsedReport = {};
                workbook.SheetNames.forEach(sheetName => {
                    parsedReport[sheetName] = parseWorkbookSheet(workbook.Sheets[sheetName]);
                });
                setParsedTableReport(parsedReport);
                setTabs([...workbook.SheetNames]);
            })
            .catch((e) => {
                console.log(e);
                message.error('Failed to download');
            }).finally(() => {
                setIsLoading(false)
            }
        )
    }

    return (
        <>
            <FullHeightCard
                title="Preview"
                extra={<LinkTextButton text="Load" onClick={handlePreviewClick}/>}
            >
                <div id="width-source" style={{height: '100%', width: '100%'}}>
                    <Spin spinning={isLoading}>
                        {tabs.length > 0 && (
                            <Tabs>
                                {tabs.map(x => (
                                    <Tabs.TabPane tab={x} key={x}>
                                        <PreviewSheet
                                            data={parsedTableReport[x]}
                                            width={document.getElementById("width-source").clientWidth}
                                        />
                                    </Tabs.TabPane>
                                ))}
                            </Tabs>
                        )}
                    </Spin>
                </div>
            </FullHeightCard>
        </>
    )
}


function PreviewSheet({data, width=600}) {
    const height = Math.min(Math.max(data.rowData.length * 28 + 48, 300), 600);
    return (
        <div className="ag-theme-alpine" style={{height: height, width: width}}>
            <AgGridReact
                suppressFieldDotNotation={true}
                rowData={data.rowData}
                columnDefs={data.columnDefs}
                pagination={data.rowData.length > 50}
                paginationPageSize={50}
                paginationPageSizeSelector={[50, 100, 500]}
            />
        </div>
    );
}

function parseWorkbookSheet(sheet) {
    if (sheet['!ref'] == null) {
        return {columnDefs: [], rowData: []};
    }

    const rows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: '',
        raw: false,
        blankrows: false,
    });
    if (rows.length === 0) {
        return {columnDefs: [], rowData: []};
    }

    const fields = rows[0].map((value, index) => {
        return value === '' || value == null ? `Column ${index + 1}` : String(value);
    });

    const columnDefs = fields.map(field => ({
        field,
        filter: true,
        sortable: true,
        resizable: true,
    }));

    const rowData = rows.slice(1).map(row => {
        const item = {};
        fields.forEach((field, index) => {
            item[field] = row[index] ?? '';
        });
        return item;
    });

    return {columnDefs, rowData};
}


function ReRun({data}) {
    const [modalVisible, setModalVisible] = useState(false);
    const history = useHistory();

    const goToReportPage = id => {
        history.push(`/reports/${id}`);
    }

    return (
        <>
            <LinkButton onClick={_ => setModalVisible(true)}>
                Re-run report
            </LinkButton>
            <CreateReportModal
                visible={modalVisible}
                updateVisibility={setModalVisible}
                onCreate={data => goToReportPage(data.id)}
                attributes={new Promise((resolve, reject) => {
                    RepApi.get(
                        `/templates/${data.template}`
                    ).then(response => {
                        resolve(extractedReportAttributes.extract({
                            template: {
                                ...mapApiTemplateModelToCreateModel(response.data),
                                parameters: data.parameters
                            }
                        }));
                    }).catch(reject)
                })}
            />
        </>
    );
}